import { ArrowDownRight, ArrowUpRight, Minus, Trophy } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "@/api";
import { FileYield, HourChart, TagChart } from "@/components/cockpit/StatsCharts";

const mmss = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round(s || 0) % 60).padStart(2, "0")}`;
const hours = (s) => { const m = Math.round((s || 0) / 60); return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}` : `${m} min`; };
const KPIS = [
  ["calls", "Appels passés"], ["answered", "Appels décrochés"], ["reach_rate", "Joignabilité", (v) => `${v} %`, "pts"], ["avg_talk", "Durée moyenne", mmss], ["talk", "Total en ligne", hours],
  ["fiches", "Fiches touchées"], ["email_out", "E-mails envoyés"], ["email_in", "E-mails reçus"], ["sms_out", "SMS envoyés"], ["sms_in", "SMS reçus"],
  ["stage_changes", "Changements d'étape"], ["rdv", "Rendez-vous obtenus"], ["ai_validated", "Actions IA validées"], ["ai_ignored", "Actions IA ignorées", null, null, true],
  ["debriefs_done", "Débriefs faits"], ["debriefs_pending", "Débriefs en attente", null, null, true],
];
const frDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", timeZone: "Europe/Paris" });
const frTime = (iso) => new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });

export const Delta = ({ cur, prev, points, lowerBetter, testid }) => {
  if (!prev && !cur) return <span className="st-delta flat" data-testid={testid}><Minus size={12} /> =</span>;
  const diff = points ? Math.round((cur - prev) * 10) / 10 : prev ? Math.round(((cur - prev) / prev) * 100) : null;
  const up = cur > prev;
  const good = cur === prev ? "flat" : up !== Boolean(lowerBetter) ? "up" : "down";
  const Icon = cur === prev ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return <span className={`st-delta ${good}`} data-testid={testid}><Icon size={12} /> {diff === null ? "nouveau" : `${diff > 0 ? "+" : ""}${diff}${points ? " pt" : " %"}`}</span>;
};

const UsersTable = ({ users, ranking }) => (
  <div className="st-table-wrap"><table className="st-table" data-testid="st-users-table">
    <thead><tr>{ranking && <th>#</th>}<th>Commercial</th><th>Appels</th><th>Décrochés</th><th>Joignab.</th><th>Durée moy.</th><th>En ligne</th><th>Fiches</th><th>E-mails env./reç.</th><th>SMS env./reç.</th><th>Étapes</th><th>RDV</th><th>IA val./ign.</th><th>Débriefs faits/att.</th></tr></thead>
    <tbody>{users.map((u, i) => (
      <tr key={u.name} data-testid={`st-user-${i}`}>
        {ranking && <td className="st-rank">{i < 3 ? <Trophy size={14} className={`r${i}`} /> : i + 1}</td>}
        <td><strong>{u.name}</strong></td><td>{u.calls}</td><td>{u.answered}</td><td>{u.reach_rate} %</td><td>{mmss(u.avg_talk)}</td><td>{hours(u.talk)}</td><td>{u.fiches}</td>
        <td>{u.email_out} / {u.email_in}</td><td>{u.sms_out} / {u.sms_in}</td><td>{u.stage_changes}</td><td><b>{u.rdv}</b></td><td>{u.ai_validated} / {u.ai_ignored}</td><td>{u.debriefs_done} / {u.debriefs_pending}</td>
      </tr>
    ))}</tbody>
  </table></div>
);

// Onglet « Statistiques » du Cockpit : respecte la période et les filtres
export const StatsPanel = ({ params }) => {
  const [d, setD] = useState(null);
  const [ranking, setRanking] = useState(() => localStorage.getItem("ck-ranking") !== "0");
  useEffect(() => { setD(null); api.get("/cockpit/stats", { params }).then(({ data }) => setD(data)).catch(() => setD({ error: true })); }, [params]);
  if (!d) return <p className="ck-muted">Calcul des statistiques…</p>;
  if (d.error) return <p className="ck-empty">Statistiques indisponibles.</p>;
  const g = d.current.global, p = d.previous.global;
  const end = new Date(new Date(d.range[1]) - 1), pend = new Date(new Date(d.previous_range[1]) - 1);
  const showRank = d.show_ranking && ranking;
  return (
    <div className="st-panel" data-testid="ck-stats">
      <p className="ck-muted" data-testid="st-compare">Du {frDate(d.range[0])} au {frDate(end)} · comparé au {frDate(d.previous_range[0])} – {d.partial ? `${frTime(d.previous_range[1])} (même durée écoulée)` : frDate(pend)}</p>
      <div className="st-kpis">
        {KPIS.map(([k, label, fmt, points, lower]) => (
          <div key={k} className="st-kpi" data-testid={`st-kpi-${k}`}>
            <span>{label}</span><strong>{fmt ? fmt(g[k]) : (g[k] || 0).toLocaleString("fr-FR")}</strong>
            <small><Delta cur={g[k] || 0} prev={p[k] || 0} points={points} lowerBetter={lower} testid={`st-delta-${k}`} /> <em>avant : {fmt ? fmt(p[k]) : p[k] || 0}</em></small>
          </div>
        ))}
      </div>
      <div className="st-charts">
        <TagChart tags={d.tags} />
        <HourChart hours={d.hours} />
      </div>
      <FileYield files={d.files} />
      <div className="st-users-head">
        <h3>Par commercial</h3>
        {d.show_ranking && <label className="ai-all"><input type="checkbox" checked={ranking} onChange={(e) => { setRanking(e.target.checked); localStorage.setItem("ck-ranking", e.target.checked ? "1" : "0"); }} data-testid="st-ranking-toggle" /> Classement</label>}
      </div>
      {d.current.users.length ? <UsersTable users={d.current.users} ranking={showRank} /> : <p className="ck-empty">Aucune activité sur cette période.</p>}
    </div>
  );
};
